import React, { useState, useEffect } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { STRIPE_PROXY_URL, PRODUCTS, PRICES, METERED_PRICES, TALK_MINUTES_PRODUCT, VIDEO_MINUTES_PRODUCT } from '../../config/billing';

interface CustomPricingModalProps {
  open: boolean;
  onClose: () => void;
  entityType: 'customer' | 'property';
  entityId: number | string;
  entityName: string;
  stripeCustomerId?: string;
  onUpdated?: () => void;
}

interface PricingRow {
  product: string;
  productName: string;
  standardPriceId: string;
  standardAmount: number;
  customAmount: string;
  override: boolean;
  isMetered: boolean;
}

const STANDARD_PRICING: PricingRow[] = [
  { product: PRODUCTS.PROPERTY_BASE, productName: 'Property Base', standardPriceId: PRICES.PROPERTY_BASE, standardAmount: 0, customAmount: '', override: false, isMetered: false },
  { product: PRODUCTS.DEVICE, productName: 'Per Device', standardPriceId: PRICES.PER_DEVICE, standardAmount: 0, customAmount: '', override: false, isMetered: false },
  { product: PRODUCTS.UNIT, productName: 'Per Unit', standardPriceId: PRICES.PER_UNIT, standardAmount: 0, customAmount: '', override: false, isMetered: false },
  { product: TALK_MINUTES_PRODUCT, productName: 'Per Talk Minute', standardPriceId: METERED_PRICES.PER_TALK_MINUTE, standardAmount: 0, customAmount: '', override: false, isMetered: true },
  { product: VIDEO_MINUTES_PRODUCT, productName: 'Per Video Minute', standardPriceId: METERED_PRICES.PER_VIDEO_MINUTE, standardAmount: 0, customAmount: '', override: false, isMetered: true },
];

export const CustomPricingModal: React.FC<CustomPricingModalProps> = ({
  open, onClose, entityType, entityId, entityName, stripeCustomerId, onUpdated,
}) => {
  const [rows, setRows] = useState<PricingRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (!open) return;
    setFetching(true);
    setError('');
    setSuccess('');

    // Fetch current standard prices to show actual amounts
    Promise.all(
      STANDARD_PRICING.map(row =>
        fetch(`${STRIPE_PROXY_URL}/prices?product=${row.product}`)
          .then(r => r.json())
          .then(prices => {
            const std = prices.find((p: any) => p.id === row.standardPriceId);
            return { ...row, standardAmount: std?.unit_amount || 0 };
          })
          .catch(() => row)
      )
    ).then(setRows).finally(() => setFetching(false));
  }, [open]);

  const toggleOverride = (idx: number) => {
    const next = [...rows];
    next[idx] = { ...next[idx], override: !next[idx].override, customAmount: next[idx].override ? '' : next[idx].customAmount };
    setRows(next);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const overrides = rows.filter(r => r.override && r.customAmount !== '');
      if (overrides.length === 0) { setError('No custom prices set'); setLoading(false); return; }

      const results = [];
      for (const row of overrides) {
        const amountCents = Math.round(parseFloat(row.customAmount) * 100);
        if (isNaN(amountCents) || amountCents < 0) continue;

        const res = await fetch(`${STRIPE_PROXY_URL}/prices`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            product: row.product,
            unit_amount: amountCents,
            currency: 'usd',
            recurring: { interval: 'month' },
            nickname: `Custom - ${row.productName} - ${entityName}`,
            metadata: {
              [`${entityType}_id`]: String(entityId),
              source: 'intrlock_custom_pricing',
              standard_price_id: row.standardPriceId,
            },
          }),
        });
        if (!res.ok) { const e = await res.json(); throw new Error(e.error || `Failed to create price for ${row.productName}`); }
        const price = await res.json();
        results.push({ product: row.productName, priceId: price.id, amount: amountCents });
      }

      setSuccess(`Created ${results.length} custom price(s). Price IDs can be used when creating subscriptions for this ${entityType}.`);
      onUpdated?.();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={`Custom Pricing — ${entityName}`} size="lg">
      {fetching ? (
        <div className="text-center py-8 text-gray-500">Loading pricing data...</div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-gray-500">
            Set custom pricing for this {entityType}. Custom prices create new Stripe price objects
            that override the standard rates when creating subscriptions.
          </p>
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-2 font-medium text-gray-600">Product</th>
                  <th className="text-right px-4 py-2 font-medium text-gray-600">Standard</th>
                  <th className="text-right px-4 py-2 font-medium text-gray-600">Custom Price</th>
                  <th className="text-center px-4 py-2 font-medium text-gray-600">Override</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr key={row.product} className="border-t border-gray-100">
                    <td className="px-4 py-3 font-medium text-gray-900">{row.productName}</td>
                    <td className="px-4 py-3 text-right text-gray-500">
                      ${(row.standardAmount / 100).toFixed(2)}/{row.isMetered ? 'unit' : 'mo'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {row.override ? (
                        <div className="flex items-center justify-end gap-1">
                          <span className="text-gray-400">$</span>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            className="w-24 border border-gray-300 rounded px-2 py-1 text-sm text-right"
                            value={row.customAmount}
                            onChange={e => {
                              const next = [...rows];
                              next[idx] = { ...next[idx], customAmount: e.target.value };
                              setRows(next);
                            }}
                            placeholder="0.00"
                          />
                        </div>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => toggleOverride(idx)}
                        className={`w-10 h-5 rounded-full transition-colors ${row.override ? 'bg-brand-600' : 'bg-gray-300'}`}
                      >
                        <div className={`w-4 h-4 bg-white rounded-full shadow transition-transform ${row.override ? 'translate-x-5' : 'translate-x-0.5'}`} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {error && <p className="text-red-600 text-sm">{error}</p>}
          {success && <p className="text-green-600 text-sm">{success}</p>}
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={onClose}>Close</Button>
            <Button onClick={handleSubmit} disabled={loading || rows.every(r => !r.override)}>
              {loading ? 'Creating...' : 'Create Custom Prices'}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};
